import logger from '../common/log'
import { PushDataCollector } from './push-data-collector'

/**
 * This class is used to decouple the PushServer from the browser client via collecting the incoming push data and
 * invoking a callback function within a given interval of time (which should update the Redux Store).
 */
export class PushDataThrottler {
    /**
     * Creates a new throttler which calls a given callback function within a given interval.
     * Please note that the used interval must be greater than the execution time of the callback function.
     *
     * @param {function(PushData)} callback
     * @param {function} getIntervalInMs
     * @param {function} getState
     */
    constructor(callback, getIntervalInMs, getState) {
        this.callback = callback
        this.getIntervalInMs = getIntervalInMs
        this.getState = getState
        this.collector = new PushDataCollector()
        this.invokeCallback = this.invokeCallback.bind(this)
    }

    /**
     * Starts the throttler. Repeated calls will result in a console error message.
     */
    start() {
        if (this.isStopped()) {
            this.startInterval()
        } else {
            logger.info('Starting not possible. Throttler is already started.')
        }
    }

    /**
     * Starts an new callback timer.
     * @private
     */
    startInterval() {
        this.timeoutId = setTimeout(this.invokeCallback, this.getIntervalInMs(this.getState))
    }

    /**
     * Returns true if the throttler is started, otherwise false.
     * @private
     * @return {boolean} true / false
     */
    isRunning() {
        return !!this.timeoutId
    }

    /**
     * Returns true if the throttler is stopped, otherwise false.
     * @private
     * @return {boolean} true / false
     */
    isStopped() {
        return !this.timeoutId
    }

    /**
     * Stops the throttler. Clears the interval set. Every consecutive call of any throttler methods,
     * but {@link start}, will result in a console error message.
     */
    stop() {
        if (this.isRunning()) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        } else {
            logger.info('Stopping not possible. Throttler is already stopped.')
        }
    }

    /**
     * Invokes the given callback if data is present within the collector, because invoking the callback
     * without data might cause unnecessary renderings on the client side (browser).
     * @private
     */
    invokeCallback() {
        if (this.collector.hasData()) {
            const data = this.collector.get()
            this.collector.clearAll()
            this.callback(data)
        }
        if (this.isRunning()) {
            this.startInterval()
        }
    }

    /**
     * Collects the data received from the Push Server.
     * @param {PushData} data
     */
    collect(data) {
        if (this.isStopped()) {
            logger.error('Collection not possible. Throttler has been shut down.')
        } else {
            this.collector.collect(data)
        }
    }

    /**
     * Discards all the collected data from the given {@link RestCollections}.
     *
     * @param {RestCollections} collections - the collections
     */
    discard(collections) {
        if (this.isStopped()) {
            logger.error('Clearance not possible. Throttler has been shut down.')
        } else {
            this.collector.clear(collections)
        }
    }
}
